import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Layout from '@shared/ui/organisms/layout/Layout';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular, BodySmall, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { DropDownListItem, ReactSelectDropDownList } from '@shared/ui/organisms/drop-down-list/ReactSelectDropDownList';
import { SimpleUser } from '@shared/models/user.models';
import AccessRequired from '@features/settings/pages/components/AccessRequired';
import { WithCurrentUser } from '@shared/hooks/WithCurrentUser';
import { isCompanyAdminUser, isOfficeAdminUser } from '@shared/utils/AccessControl';
import { loadAllUsers, selectAllUsers } from '@features/settings/store/userManagement.duck';
import { useLockersEnabled } from './data/UseLockersEnabled';
import { useLockerOffices } from './data/UseLockerOffices';
import {
  assignLocker,
  leaveWaitingList,
  loadLockerSummary,
  selectAvailableLockers,
  selectIsLockersLoading,
  selectIsLockersSaving,
  selectLockerSummary,
  selectLockerWaitingList
} from './data/lockers.duck';
import { Locker, LockerOfficeSummary, WaitingListEntry, WaitingListSort } from './data/lockers.models';
import { averageDaysWaiting, daysWaiting, formatDate, initialsFor, longestWait } from './data/lockers.utils';
import { AddToWaitingListDialog } from './components/AddToWaitingListDialog';
import { FreeLockerMenu } from './components/FreeLockerMenu';
import {
  EmptyState,
  HeroCardBody,
  IconTile,
  InitialsAvatar,
  LockerBodyLayout,
  LockerCard,
  LockerCardFooter,
  LockerMainColumn,
  LockerPageBody,
  LockerRail,
  LockerTitleActions,
  LockerTitleRow,
  NarrowSubtitle,
  OfficeSelectWrapper,
  RowAction,
  SearchField,
  SearchInput,
  StatusDot,
  StatusPill,
  TableCell,
  TableHeaderRow,
  TableRow,
  TableRowActions,
  TableToolbar,
  WideCell
} from './components/LockerViewComponents';

const QUEUE_COLUMNS = '48px minmax(0, 1fr) minmax(0, 170px) minmax(0, 140px) minmax(0, 130px) 230px';
const AVAILABLE_TILE = 'rgba(32, 204, 111, 0.12)';
const WAITING_PILL_BACKGROUND = 'rgba(240, 130, 34, 0.12)';
const PERSONAL_BORDER = 'rgba(0, 87, 255, 0.25)';
const LONG_WAIT_DAYS = 30;

/**
 * Design 3a. The queue in full, for one office. The thing an admin is really asking here is "who
 * has been forgotten", so a wait over 30 days is called out rather than left as a date to subtract.
 */
export function LockerWaitingListPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = WithCurrentUser();
  const lockersEnabled = useLockersEnabled();

  const canManage = isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser);
  const { officeOptions, selectedOfficeOption, selectedOfficeId, selectedOfficeName, selectOffice } =
    useLockerOffices(lockersEnabled && canManage);

  const waitingList = useSelector(selectLockerWaitingList);
  const freeLockers = useSelector(selectAvailableLockers);
  const summary = useSelector(selectLockerSummary);
  const allUsers = useSelector(selectAllUsers);
  const isLoading = useSelector(selectIsLockersLoading);
  const isSaving = useSelector(selectIsLockersSaving);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<WaitingListSort>(WaitingListSort.Position);
  const [isAddingPerson, setIsAddingPerson] = useState(false);

  useEffect(() => {
    if (!lockersEnabled || !canManage) return;
    dispatch(loadLockerSummary());
    // Team names are not part of a waiting-list entry, so they come from the company user list.
    dispatch(loadAllUsers());
  }, [dispatch, lockersEnabled, canManage]);

  const teamNamesByUserId = useMemo(() => {
    return allUsers.reduce((teams: { [userId: string]: string }, user: SimpleUser) => {
      if (user.teamName) teams[user.userId] = user.teamName;
      return teams;
    }, {});
  }, [allUsers]);

  const entries = waitingList?.entries ?? [];

  const visibleEntries = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const matching = entries.filter((entry: WaitingListEntry) =>
      !query || (entry.userName ?? '').toLowerCase().includes(query));

    // The queue is FIFO by position from the backend; the other orders are ways of reading it.
    return [...matching].sort((a: WaitingListEntry, b: WaitingListEntry) => {
      if (sortBy === WaitingListSort.Name) return (a.userName ?? '').localeCompare(b.userName ?? '');
      if (sortBy === WaitingListSort.LongestWaiting) return daysWaiting(b) - daysWaiting(a);
      return a.position - b.position;
    });
  }, [entries, searchTerm, sortBy]);

  const otherOffices = useMemo(() => {
    return summary.filter((office: LockerOfficeSummary) => office.officeId !== selectedOfficeId);
  }, [summary, selectedOfficeId]);

  const sortOptions: DropDownListItem[] = [
    { value: WaitingListSort.Position, label: t('lockers.sort-queue-position') },
    { value: WaitingListSort.LongestWaiting, label: t('lockers.sort-longest-waiting') },
    { value: WaitingListSort.Name, label: t('lockers.sort-name') },
  ];

  const onAssign = (entry: WaitingListEntry, locker: Locker) => {
    // Assigning drops the person's queue entry server-side, so there is nothing to tidy up here.
    if (!entry.userId) return;
    dispatch(assignLocker({ lockerId: locker.id, userId: entry.userId }));
  };

  if (!lockersEnabled || !canManage) {
    return (
      <Layout>
        <AccessRequired visible={true} companyAdminsRequired={true} />
      </Layout>
    );
  }

  const nextOutOfReach = entries.find((entry: WaitingListEntry) => entry.position === freeLockers.length + 1);
  const subtitle = [
    t('lockers.people-waiting-at', { count: waitingList?.totalWaiting ?? 0, office: selectedOfficeName }),
    t('lockers.longest-wait', { count: longestWait(entries) }),
    t('lockers.free-count', { count: freeLockers.length }),
  ].join(' · ');

  return (
    <Layout hideFooter={true} style={{ backgroundColor: 'white' }}>
      <LockerPageBody>
        <GoBackButton url={'/whereabouts/company/lockers'} text={'lockers.title'}
                      style={{ marginBottom: 0 }} />

        <LockerTitleRow>
          <div>
            <HeadlineMedium style={{ marginBottom: 4 }}>{t('lockers.waiting-list')}</HeadlineMedium>
            <BodySmall colour={Colours.darkGrey}>{subtitle}</BodySmall>
          </div>

          <LockerTitleActions>
            <OfficeSelectWrapper>
              <BodySmall colour={Colours.darkGrey}>{t('lockers.office')}</BodySmall>
              <ReactSelectDropDownList items={officeOptions}
                                       selectedItem={selectedOfficeOption}
                                       placeholder={'lockers.select-office'}
                                       onItemSelected={(item: DropDownListItem) => selectOffice(Number(item.value))} />
            </OfficeSelectWrapper>

            <OutlineButton text={'lockers.add-someone-to-list'} icon={IconTypes.Add} size={ButtonSize.Medium}
                           click={() => setIsAddingPerson(true)} />
          </LockerTitleActions>
        </LockerTitleRow>

        {isAddingPerson && !!selectedOfficeId &&
          <AddToWaitingListDialog officeId={selectedOfficeId}
                                  officeName={selectedOfficeName}
                                  onClose={() => setIsAddingPerson(false)} />}

        {freeLockers.length > 0 && entries.length > 0 && (
          <LockerCard accent={PERSONAL_BORDER}>
            <HeroCardBody style={{ padding: 16, gap: 16 }}>
              <IconTile tileSize={40} backgroundColour={AVAILABLE_TILE}>
                <Icon icon={IconTypes.Padlock} style={{ color: Colours.darkGreen }} />
              </IconTile>

              <div style={{ flex: 1, minWidth: 0 }}>
                <BodyRegular weight={600}>{t('lockers.free-right-now', { count: freeLockers.length })}</BodyRegular>
                <BodySmall colour={Colours.darkGrey}>
                  {nextOutOfReach
                    ? t('lockers.assigning-top-clears-to', {
                        count: Math.min(freeLockers.length, entries.length),
                        name: nextOutOfReach.userName,
                      })
                    : t('lockers.assigning-top-clears-queue', { count: Math.min(freeLockers.length, entries.length) })}
                </BodySmall>
              </div>
            </HeroCardBody>
          </LockerCard>
        )}

        <LockerBodyLayout>
          <LockerMainColumn>
            <LockerCard>
              <TableToolbar>
                <SearchField>
                  <Icon icon={IconTypes.Search} style={{ color: Colours.darkGrey }} />
                  <SearchInput value={searchTerm}
                               onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                               placeholder={t('lockers.search-the-queue')}
                               aria-label={t('lockers.search-the-queue')} />
                </SearchField>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BodySmall colour={Colours.darkGrey}>{t('lockers.sort')}</BodySmall>
                  <div style={{ minWidth: 200 }}>
                    <ReactSelectDropDownList items={sortOptions}
                                             selectedItem={sortOptions.find(option => option.value === sortBy)}
                                             onItemSelected={(item: DropDownListItem) => setSortBy(item.value)} />
                  </div>
                </div>
              </TableToolbar>

              <TableHeaderRow columns={QUEUE_COLUMNS}>
                <BodyVerySmall weight={600} colour={Colours.darkGrey}>#</BodyVerySmall>
                <BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('lockers.person')}</BodyVerySmall>
                <BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('lockers.team')}</BodyVerySmall>
                <BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('lockers.joined')}</BodyVerySmall>
                <BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('lockers.waiting')}</BodyVerySmall>
                <BodyVerySmall weight={600} colour={Colours.darkGrey}
                               style={{ textAlign: 'right' }}>{t('lockers.actions')}</BodyVerySmall>
              </TableHeaderRow>

              {isLoading && <SimpleSpinner size={40} />}

              {!isLoading && visibleEntries.length === 0 && (
                <EmptyState>
                  <BodyRegular colour={Colours.darkGrey}>
                    {entries.length === 0 ? t('lockers.waiting-list-empty') : t('lockers.no-matching-people')}
                  </BodyRegular>
                  {entries.length > 0 && !!searchTerm &&
                    <RowAction width={140} style={{ margin: '12px auto 0' }}>
                      <OutlineButton text={'lockers.clear-search'} size={ButtonSize.Small}
                                     click={() => setSearchTerm('')} />
                    </RowAction>}
                </EmptyState>
              )}

              {!isLoading && visibleEntries.map((entry: WaitingListEntry) => {
                const days = daysWaiting(entry);
                const isLongWait = days > LONG_WAIT_DAYS;

                return (
                  <TableRow key={entry.id} columns={QUEUE_COLUMNS}>
                    <WideCell>
                      <BodyRegular weight={700} colour={Colours.darkGrey}>{entry.position}</BodyRegular>
                    </WideCell>

                    <TableCell>
                      <InitialsAvatar size={32}>{initialsFor(entry.userName)}</InitialsAvatar>
                      <div style={{ minWidth: 0 }}>
                        <BodyRegular weight={600}>{entry.userName}</BodyRegular>
                        <NarrowSubtitle>
                          <BodySmall colour={Colours.darkGrey}>
                            {[
                              entry.userId ? teamNamesByUserId[entry.userId] : null,
                              t('lockers.waiting-days', { count: days }),
                            ].filter(Boolean).join(' · ')}
                          </BodySmall>
                        </NarrowSubtitle>
                      </div>
                    </TableCell>

                    <WideCell>
                      <BodySmall colour={Colours.darkGrey}>
                        {(entry.userId ? teamNamesByUserId[entry.userId] : '') || '—'}
                      </BodySmall>
                    </WideCell>

                    <WideCell>
                      <BodySmall colour={Colours.darkGrey}>{formatDate(entry.createdAt)}</BodySmall>
                    </WideCell>

                    <WideCell>
                      {isLongWait
                        ? <StatusPill backgroundColour={WAITING_PILL_BACKGROUND}>
                            <StatusDot colour={Colours.orange} />
                            <BodyVerySmall weight={600} colour={Colours.orange}>
                              {t('lockers.waiting-days', { count: days })}
                            </BodyVerySmall>
                          </StatusPill>
                        : <BodySmall colour={Colours.darkGrey}>{t('lockers.waiting-days', { count: days })}</BodySmall>}
                    </WideCell>

                    <TableRowActions>
                      <FreeLockerMenu freeLockers={freeLockers} disabled={isSaving}
                                      onSelect={(locker: Locker) => onAssign(entry, locker)} />
                      <RowAction width={100}>
                        <OutlineButton text={'lockers.remove'} size={ButtonSize.Small} disabled={isSaving}
                                       borderColour={Colours.mildGrey}
                                       click={() => dispatch(leaveWaitingList(entry.id))} />
                      </RowAction>
                    </TableRowActions>
                  </TableRow>
                );
              })}

              {entries.length > 0 && (
                <LockerCardFooter>
                  <BodySmall colour={Colours.darkGrey}>
                    {t('lockers.showing-all-waiting', { count: entries.length, office: selectedOfficeName })}
                  </BodySmall>
                  <BodySmall colour={Colours.darkGrey}>
                    {t('lockers.average-wait', { count: averageDaysWaiting(entries) })}
                  </BodySmall>
                </LockerCardFooter>
              )}
            </LockerCard>
          </LockerMainColumn>

          <LockerRail>
            <LockerCard>
              <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <BodyLarge weight={600}>{t('lockers.queues-elsewhere')}</BodyLarge>

                {otherOffices.length === 0 &&
                  <BodySmall colour={Colours.darkGrey}>{t('lockers.no-other-offices')}</BodySmall>}

                {otherOffices.map((office: LockerOfficeSummary) => {
                  const isStuck = office.availableLockers === 0 && office.waitingListCount > 0;

                  return (
                    <div key={office.officeId}
                         style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <BodyRegular colour={Colours.darkGrey}>{office.officeName}</BodyRegular>
                      {office.waitingListCount > 0
                        ? <BodyRegular weight={600} colour={isStuck ? Colours.orange : Colours.black}>
                            {t('lockers.waiting-count', { count: office.waitingListCount })}
                          </BodyRegular>
                        : <BodyRegular colour={Colours.darkGrey}>{t('lockers.no-queue')}</BodyRegular>}
                    </div>
                  );
                })}
              </div>
            </LockerCard>
          </LockerRail>
        </LockerBodyLayout>
      </LockerPageBody>
    </Layout>
  );
}
